import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, primeCsrf } from './api';
import type { NotificationData, NotificationPreferencesData } from '@/types/api';

const LIST_KEY = (filter?: 'unread') => ['notifications', filter ?? 'all'];
const COUNT_KEY = ['notifications', 'unread-count'] as const;

export function useNotifications(filter?: 'unread') {
  return useQuery({
    queryKey: LIST_KEY(filter),
    queryFn: async (): Promise<NotificationData[]> => {
      const { data } = await api.get<NotificationData[]>('/v1/notifications', {
        params: filter ? { filter } : undefined,
      });
      return data;
    },
  });
}

export function useUnreadNotificationsCount() {
  return useQuery({
    queryKey: COUNT_KEY,
    queryFn: async (): Promise<number> => {
      const { data } = await api.get<{ count: number }>('/v1/notifications/unread-count');
      return data.count;
    },
    refetchInterval: 30_000, // poll every 30s
    refetchIntervalInBackground: false,
  });
}

export function useMarkNotificationRead() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string): Promise<void> => {
      await primeCsrf();
      await api.post(`/v1/notifications/${id}/read`);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['notifications'] });
    },
  });
}

export function useMarkAllNotificationsRead() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (): Promise<void> => {
      await primeCsrf();
      await api.post('/v1/notifications/read-all');
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['notifications'] });
    },
  });
}

export function useDeleteNotification() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string): Promise<void> => {
      await primeCsrf();
      await api.delete(`/v1/notifications/${id}`);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['notifications'] });
    },
  });
}

export function useClearAllNotifications() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (): Promise<void> => {
      await primeCsrf();
      await api.post('/v1/notifications/clear');
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['notifications'] });
    },
  });
}

const PREFS_KEY = ['notifications', 'preferences'] as const;

/** The subscribable event catalogue plus this user's on/off state for each. */
export function useNotificationPreferences() {
  return useQuery({
    queryKey: PREFS_KEY,
    queryFn: async (): Promise<NotificationPreferencesData> => {
      const { data } = await api.get<NotificationPreferencesData>('/v1/me/notification-preferences');
      return data;
    },
  });
}

/**
 * Flip one or more subscriptions. The server merges a partial map over what's
 * stored, so a single toggle sends only itself.
 */
export function useUpdateNotificationPreferences() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (subscribed: Record<string, boolean>): Promise<NotificationPreferencesData> => {
      await primeCsrf();
      const { data } = await api.patch<NotificationPreferencesData>(
        '/v1/me/notification-preferences',
        { subscribed },
      );
      return data;
    },
    onSuccess: (data) => qc.setQueryData(PREFS_KEY, data),
  });
}
